import React from "react";
import { Input, Form } from "antd";
import { css } from "glamor";
import Button from "@arachnys/styleguide/Button";
import Tabs from "@arachnys/styleguide/Tabs";
import { withProps } from "@arachnys/flow-app/src/util";
import PropTypes from "prop-types";
import TaggingToolbar from "../TaggingToolbar";

const styles = {
    overlayWrapper: css({
        bottom: "0",
        display: "block",
        minHeight: "65px",
        position: "fixed",
        // compensate 50px left and right screen margins
        width: "calc(100% - 100px)",
        pointerEvents: "none",
        zIndex: "5",
    }),
    overlayInner: css({
        background: "#1b75bc",
        padding: "13px 16px 0",
        borderTop: "1px solid #e0e0e0",
        pointerEvents: "auto",
        borderRadius: "0 5px 5px",

        " .ant-form-item-label": {
            lineHeight: "24px",
        },
        " .ant-form-item-label label": {
            color: "#fff",
            fontSize: "14px",
        },
    }),
    taggingToolbarSelectContainer: css({
        marginRight: 25,
    }),
    underContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    }),
    commentContainer: css({
        color: "white",
        flexGrow: "1",
        marginRight: "20px",
        marginBottom: "20px",
    }),
    commentInput: css({
        height: "85px !important",
        marginTop: "5px",
    }),
    tabs: css({
        " .ant-tabs-nav": {
            pointerEvents: "auto",
        },
        " .ant-tabs-bar": {
            border: "none",
            margin: "0",
        },
        " .ant-tabs-tab": {
            backgroundColor: "rgb(225, 243, 253) !important",
        },
        " .ant-tabs-tab-active": {
            color: "#FFF !important",
            backgroundColor: "#1b75bc !important",
        },
    }),
    applyButtonContainer: css({
        alignSelf: "flex-end",
        marginBottom: 25,
    }),
    applyButton: css({
        width: 150,
    }),
    counter: css({
        color: "#fff",
        fontSize: "12px",
        textAlign: "center",
        lineHeight: "22px",
    }),
};

const DISPOSITION_NAME = "Change disposition";
const REVIEW_NAME = "Review";

const OverlayBar = ({ children, staticPosition }) => {
    return (
        <div className={staticPosition ? "" : `${styles.overlayWrapper}`}>
            {children}
        </div>
    );
};

OverlayBar.propTypes = {
    children: PropTypes.element.isRequired,
};

const OverlayInner = ({ children }) => {
    return <div className={`${styles.overlayInner}`}>{children}</div>;
};

OverlayInner.propTypes = {
    children: PropTypes.element.isRequired,
};

class DispositionForm extends React.Component {
    constructor(props) {
        super(props);

        this.defaultState = {
            comment: "",
            tags: [],
        };

        this.state = { ...this.defaultState };
    }

    static propTypes = {
        config: PropTypes.object.isRequired,
        apply: PropTypes.func.isRequired,
        count: PropTypes.number,
        isReview: PropTypes.bool,
    };

    isDisabled() {
        return this.state.tags.length === 0 && this.state.comment === "";
    }

    updateComment = event => {
        this.setState({ comment: event.target.value });
    };

    updateTags = selectedTags => {
        this.setState({ tags: selectedTags });
    };

    clickApply = () => {
        this.props.apply(this.state.tags, this.state.comment);
        this.setState(this.defaultState);
        this.clearToolbarTags();
    };

    render() {
        return (
            <div>
                <TaggingToolbar
                    selectedTags={this.state.tags}
                    onNew={this.updateTags}
                    onUpdate={this.updateTags}
                    taggingConfig={this.props.config}
                    clearTagsRef={clearTags => {
                        this.clearToolbarTags = clearTags;
                    }}
                    wrapper={withProps(Form.Item, {
                        className: `${styles.taggingToolbarSelectContainer}`,
                    })}
                    isReview={this.props.isReview}
                />
                <div className={`${styles.underContainer}`}>
                    <div className={`${styles.commentContainer}`}>
                        <label htmlFor="comment">Comments:</label>
                        <Input.TextArea
                            className={`${styles.commentInput}`}
                            id="comment"
                            onChange={this.updateComment}
                            value={this.state.comment}
                            placeholder="Enter your comment"
                            data-testid="disposition-overlay-comment-textarea"
                        />
                    </div>

                    <div className={`${styles.applyButtonContainer}`}>
                        <Button.Ghost
                            className={`${styles.applyButton}`}
                            size="large"
                            darkbg
                            onClick={this.clickApply}
                            disabled={this.isDisabled()}
                            data-testid="disposition-overlay-apply-button"
                        >
                            Apply
                        </Button.Ghost>
                        <div className={`${styles.counter}`}>
                            {`${this.props.count} result${
                                this.props.count > 1 ? "s" : ""
                            } selected`}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

class DispositionOverlayBar extends React.Component {
    static propTypes = {
        taggingConfig: PropTypes.object.isRequired,
        reviewTaggingConfig: PropTypes.object,
        applyDispositions: PropTypes.func.isRequired,
        selectedCount: PropTypes.number,
        changeDispositionReviewMode: PropTypes.func,
        dispositionReviewMode: PropTypes.bool,
        staticPosition: PropTypes.bool,
    };

    getLabel = index => (index ? DISPOSITION_NAME : REVIEW_NAME);

    changeTab = label => {
        this.props.changeDispositionReviewMode(label === REVIEW_NAME);
    };

    render() {
        if (this.props.reviewTaggingConfig) {
            const configs = [
                this.props.reviewTaggingConfig,
                this.props.taggingConfig,
            ];
            const tabsConfig = configs.map((config, index) => ({
                label: this.getLabel(index),
                content: (
                    <OverlayInner>
                        <DispositionForm
                            config={config}
                            count={this.props.selectedCount}
                            apply={this.props.applyDispositions}
                        />
                    </OverlayInner>
                ),
            }));

            return (
                <OverlayBar staticPosition={this.props.staticPosition}>
                    <Tabs
                        tabs={tabsConfig}
                        style={styles.tabs}
                        activeKey={this.getLabel(
                            !this.props.dispositionReviewMode
                        )}
                        onChange={this.changeTab}
                    />
                </OverlayBar>
            );
        } else {
            return (
                <OverlayBar staticPosition={this.props.staticPosition}>
                    <OverlayInner>
                        <DispositionForm
                            config={this.props.taggingConfig}
                            count={this.props.selectedCount}
                            apply={this.props.applyDispositions}
                        />
                    </OverlayInner>
                </OverlayBar>
            );
        }
    }
}

export default DispositionOverlayBar;
